import { Component, computed, inject, signal, OnInit } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { environment } from '../../../environments/environment';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { SPEND_HINTS } from '../../core/courier-spend-hints';
import { DeliveryCostDetail, DeliveryCostReport, DeliveryStoreRow, DeliveryTraceRow, DeliveryTraceSettings, DoorDashConnection, OnboardedLocation } from '../../core/models';

// THE DOORDASH MAINTAINER. The middleware — not the storefront — holds the Drive credentials now, because
// a quote asked for at checkout must not be COMMITTED until the payment behind it is confirmed, and only
// this side knows when that happens. So this page is where the connection lives, where each merchant's
// DoorDash store id is set, and where the courier spend is read.
//
// Three things worth knowing while using it:
//   * Drive authenticates a DEVELOPER, not a store: one connection serves every merchant, and the merchant
//     travels in each payload as its external store id (below).
//   * The signing secret is write-only. The form never shows the stored value and leaving it blank keeps
//     it — the same convention as the XT Auth-Key and the payment methods.
//   * A location with no store id cannot be quoted. That is deliberate: quoting from a guessed id books a
//     courier at the wrong door.
//
// The page is TABBED because it holds four unrelated jobs — the credentials, the merchant stores, the
// spend, and the quote notebook — and the notebook is the one nobody should stumble into: it is off by
// default and, while on, records customers' delivery addresses. Every tab except Diagnostics loads exactly
// as it always did on page open, so operators' muscle memory is unchanged; Diagnostics is the only tab
// that waits to be asked (see `visited`), because its first question of the server is "is tracing on".
@Component({
    selector: 'app-doordash-integration',
    standalone: true,
    imports: [
        CardModule, ButtonModule, InputTextModule, InputNumberModule, MessageModule, SelectModule,
        TableModule, TabsModule, TagModule, ToggleSwitchModule, TooltipModule, SelectButtonModule,
        FormsModule, DatePipe, DecimalPipe
    ],
    template: `
        <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
        <p-tablist>
            <p-tab value="connection">Connection</p-tab>
            <p-tab value="stores">Stores</p-tab>
            <p-tab value="spend">Spend</p-tab>
            <p-tab value="diagnostics">Diagnostics</p-tab>
        </p-tablist>
        <p-tabpanels>
        <p-tabpanel value="connection">
        <p-card header="DoorDash Integration">

            @if (loadError()) { <p-message severity="error" styleClass="mb-3">{{ loadError() }}</p-message> }

            <div class="flex flex-col gap-3 max-w-xl">
                <div class="flex flex-col gap-1">
                    <label for="dd-url" class="text-sm font-medium">Drive API base URL</label>
                    <input pInputText id="dd-url" [(ngModel)]="apiBaseUrl" [placeholder]="SANDBOX_URL" />
                    <small class="text-surface-500">
                        <button type="button" class="underline text-primary" (click)="apiBaseUrl = SANDBOX_URL">Sandbox</button>
                        · no dasher is ever dispatched.
                        <button type="button" class="underline text-primary ml-1" (click)="apiBaseUrl = PRODUCTION_URL">Production</button>
                        · dispatches a real DoorDasher.
                    </small>
                </div>

                <div class="grid grid-cols-2 gap-3">
                    <div class="flex flex-col gap-1">
                        <label for="dd-dev" class="text-sm font-medium">Developer ID</label>
                        <input pInputText id="dd-dev" [(ngModel)]="developerId" placeholder="the JWT's iss" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label for="dd-key" class="text-sm font-medium">Key ID</label>
                        <input pInputText id="dd-key" [(ngModel)]="keyId" placeholder="the JWT's kid" />
                    </div>
                </div>

                <div class="flex flex-col gap-1">
                    <label for="dd-secret" class="text-sm font-medium">Signing secret<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Paste it exactly as DoorDash issued it." tooltipPosition="top"></i>
                        @if (connection()?.hasSigningSecret) { <p-tag value="set" severity="success" styleClass="ml-2" /> }
                    </label>
                    <input pInputText id="dd-secret" type="password" [(ngModel)]="signingSecret"
                           [placeholder]="connection()?.hasSigningSecret ? '•••••• (unchanged)' : 'required'" />
                </div>

                <div class="grid grid-cols-2 gap-3">
                    <div class="flex flex-col gap-1">
                        <label for="dd-biz" class="text-sm font-medium">External business ID</label>
                        <input pInputText id="dd-biz" [(ngModel)]="externalBusinessId" placeholder="optional" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <label for="dd-prefix" class="text-sm font-medium">Installation ID prefix<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Put in front of the store and business IDs registered at DoorDash, so installations sharing one account do not clash." tooltipPosition="top"></i></label>
                        <input pInputText id="dd-prefix" [(ngModel)]="idPrefix" placeholder="lc" class="w-40" />
                    </div>

                    <div class="flex flex-col gap-1">
                        <label for="dd-exp" class="text-sm font-medium">Quote validity (minutes)<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="How long a delivery fee may be shown before it is quoted again." tooltipPosition="top"></i></label>
                        <p-inputNumber inputId="dd-exp" [(ngModel)]="quoteValidMinutes" [min]="1" [max]="60" [showButtons]="true" />
                    </div>
                </div>

                @if (saveError()) { <p-message severity="error">{{ saveError() }}</p-message> }
                @if (saved()) { <p-message severity="success">Connection saved.</p-message> }

                <div class="flex items-center gap-2">
                    <p-button label="Save" icon="pi pi-save" [loading]="saving()" (onClick)="save()" />
                    @if (connection()?.lastModified) {
                        <span class="text-xs text-surface-500">Last updated {{ connection()!.lastModified | date: 'medium' }}</span>
                    }
                </div>
            </div>
        </p-card>

        <!-- THE ONE PIECE OF THIS INTEGRATION THAT LIVES IN DOORDASH'S PORTAL, and the one that failed
             silently: a webhook registered against the wrong path is refused before any of our code runs,
             and the only trace is a status code in DoorDash's own log. Stating the exact URL here is
             cheaper than diagnosing it again. It sits beside the credentials because it is the other half
             of "connect this platform to DoorDash". -->
        <p-card styleClass="mt-4">
            <ng-template pTemplate="title"><span>Webhook URL</span><i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Register this URL in DoorDash's developer portal. Delivery updates, pickups and cancellations arrive through it." tooltipPosition="top"></i></ng-template>

            <div class="flex items-center gap-2 mb-2">
                <input pInputText [value]="webhookUrl" readonly class="w-full font-mono text-xs" />
                <p-button icon="pi pi-copy" size="small" [text]="true"
                          (onClick)="copyWebhookUrl()" />
                <i class="pi pi-info-circle text-surface-400 cursor-help" pTooltip="Before registering, add ?token= followed by a token from Admin → API Tokens to the end of this URL." tooltipPosition="top"></i>
            </div>
            @if (copied()) { <p-message severity="success" styleClass="mb-2">Copied.</p-message> }

        </p-card>
        </p-tabpanel>

        <p-tabpanel value="stores">
        <p-card>
            <ng-template pTemplate="title"><span>Merchant stores</span><i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="A merchant must be registered at DoorDash to get delivery quotes. Couriers collect from the address DoorDash holds." tooltipPosition="top"></i></ng-template>

            @if (storeError()) { <p-message severity="error" styleClass="mb-3">{{ storeError() }}</p-message> }

            <p-table [value]="locations()" [loading]="locationsLoading()" styleClass="p-datatable-sm">
                <div class="flex items-center gap-2 mb-3">
                    <p-button label="Re-check at DoorDash" icon="pi pi-verified" size="small" [outlined]="true"
                              [loading]="verifying()" (onClick)="loadStores(true)" />
                </div>

                <ng-template pTemplate="header">
                    <tr><th>Merchant</th><th>Location</th><th>DoorDash store id</th><th>At DoorDash</th><th class="w-40"></th></tr>
                </ng-template>
                <ng-template pTemplate="body" let-l>
                    <tr>
                        <td>{{ l.name || '—' }} <span class="text-surface-400 text-xs">{{ l.merchantCode }}</span></td>
                        <td class="font-mono text-xs">{{ l.locationId }}</td>
                        <!-- DERIVED, NOT TYPED. The middleware composes this id from the location and
                             refuses a supplied one (422), because it must equal the id Magento registered the
                             store under — a hand-typed lc_store_techwriters against a store DoorDash holds as
                             lc_store_<locationId> is the failure this field used to invite. Register asks
                             DoorDash which id it actually has and records the answer, so there is nothing
                             left here for an operator to set. -->
                        <td class="font-mono text-xs">
                            @if (storeRow(l.locationId)?.doorDashStoreId; as id) {
                                {{ id }}
                            } @else {
                                <span class="text-surface-400">assigned on register</span>
                            }
                        </td>
                        <td>
                            <!-- Our id vs DoorDash's answer, deliberately separate: a store id nobody
                                 registered reads as configured and then fails at checkout. -->
                            <p-tag [value]="storeStatusLabel(l.locationId)" [severity]="storeStatusSev(l.locationId)" />
                            @if (storeRow(l.locationId)?.detail; as why) {
                                <div class="text-xs text-surface-500 mt-1">{{ why }}</div>
                            }
                            @if (storeRow(l.locationId); as row) {
                                @if (row.registeredAddress || row.registeredName) {
                                    <!-- DoorDash's OWN record: this is the address a dasher collects from, so
                                         it is shown next to ours rather than assumed to match. -->
                                    <div class="text-xs mt-1">
                                        <span class="text-surface-500">At DoorDash:</span>
                                        {{ row.registeredName || '—' }}
                                        @if (row.registeredPhone) { <span class="text-surface-500">· {{ row.registeredPhone }}</span> }
                                        @if (row.registeredAddress) { <div class="text-surface-500">{{ row.registeredAddress }}</div> }
                                        @if (addressDiffers(l, row)) {
                                            <div class="text-amber-600 dark:text-amber-400">
                                                ⚠ differs from the merchant record ({{ l.address }}) — DoorDash collects from ITS copy
                                            </div>
                                        }
                                    </div>
                                }
                            }
                        </td>
                        <td class="whitespace-nowrap">
                            <!-- Register CREATES the store; Update rewrites the one DoorDash already has.
                                 Never both: a Register button beside a registered merchant is an invitation
                                 to overwrite a live store's details, and an Update on a store that does not
                                 exist has nothing to write to. -->
                            @if (needsRegistering(l.locationId)) {
                                <p-button label="Register" size="small" [text]="true" severity="secondary"
                                          [loading]="registering() === l.locationId" (onClick)="register(l)" />
                            } @else if (storeRow(l.locationId); as row) {
                                <!-- LOUD when the copies disagree — that row is a dasher at the wrong door —
                                     and quiet but present when they match, because a phone or a name can
                                     have changed without the address moving. -->
                                <p-button label="Update at DoorDash" icon="pi pi-cloud-upload" size="small"
                                          [text]="!addressDiffers(l, row)"
                                          [outlined]="addressDiffers(l, row)"
                                          [severity]="addressDiffers(l, row) ? 'warn' : 'secondary'"
                                          [loading]="updating() === l.locationId"
                                          [disabled]="updating() !== null"
                                          [pTooltip]="UPDATE_HINT"
                                          (onClick)="updateDetails(l)" />
                                @if (updated() === l.locationId) {
                                    <div class="text-xs text-green-600 dark:text-green-400 mt-1">
                                        Sent to DoorDash.
                                    </div>
                                }
                                <!-- Beside the button that caused it, not in the card header: with one row
                                     per merchant, a banner at the top does not say which one failed. -->
                                @if (updateFailed(); as failure) {
                                    @if (failure.locationId === l.locationId) {
                                        <p-message severity="error" styleClass="mt-1 text-xs">{{ failure.message }}</p-message>
                                    }
                                }
                            }
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr><td colspan="4">No onboarded locations.</td></tr>
                </ng-template>
            </p-table>
        </p-card>
        </p-tabpanel>

        <p-tabpanel value="spend">
        <p-card header="Courier spend">

            <div class="flex flex-wrap items-end gap-2 mb-3">
                <div class="flex flex-col gap-1">
                    <label for="dd-from" class="text-xs text-surface-500">From</label>
                    <input pInputText id="dd-from" type="date" [(ngModel)]="from" />
                </div>
                <div class="flex flex-col gap-1">
                    <label for="dd-to" class="text-xs text-surface-500">To</label>
                    <input pInputText id="dd-to" type="date" [(ngModel)]="to" />
                </div>
                <p-button label="Run" icon="pi pi-chart-bar" size="small" [loading]="costsLoading()" (onClick)="loadCosts()" />
            </div>

            @if (costError()) { <p-message severity="error" styleClass="mb-3">{{ costError() }}</p-message> }

            @if (costs(); as report) {
                <!-- Separated, not pre-summed: the quote cost and the driver's tip are different money,
                     and what the shopper paid is a third number that a re-price can move away from the fee.
                     Six numbers that read alike and are not — each label carries its definition as a
                     tooltip (SPEND_HINTS; the rules live in CourierSpend, server-side). -->
                <div class="flex flex-wrap gap-6 mb-3 text-sm">
                    <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.fee">Quote fees</span>
                        <span class="font-semibold tabular-nums ml-1">{{ report.totalFees | number: '1.2-2' }}</span></div>
                    <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.tip">Dasher tips</span>
                        <span class="font-semibold tabular-nums ml-1">{{ report.totalTips | number: '1.2-2' }}</span></div>
                    <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.paid">Paid to DoorDash</span>
                        <span class="font-semibold tabular-nums ml-1">{{ report.totalPaidToCourier | number: '1.2-2' }}</span></div>
                    <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.charged">Charged to shoppers</span>
                        <span class="font-semibold tabular-nums ml-1">{{ report.totalChargedToCustomers | number: '1.2-2' }}</span></div>
                    @if (report.totalAbsorbed !== 0) {
                        <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.absorbed">Absorbed</span>
                            <span class="font-semibold tabular-nums ml-1" [class.text-orange-500]="report.totalAbsorbed > 0">
                                {{ report.totalAbsorbed | number: '1.2-2' }}</span></div>
                    }
                    @if (report.totalVoidedDeliveries > 0) {
                        <div><span class="text-surface-500 cursor-help" [pTooltip]="HINTS.billed">Not billed</span>
                            <span class="font-semibold tabular-nums ml-1">{{ report.totalVoidedDeliveries }}</span>
                            <span class="text-surface-500 ml-1">({{ report.totalVoidedAmount | number: '1.2-2' }})</span></div>
                    }
                </div>

                <p-table [value]="report.merchants" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Merchant</th><th>Deliveries</th>
                            <th class="cursor-help" [pTooltip]="HINTS.fee">Quote fees</th>
                            <th class="cursor-help" [pTooltip]="HINTS.tip">Tips</th>
                            <th class="cursor-help" [pTooltip]="HINTS.paid">Paid to DoorDash</th>
                            <th class="cursor-help" [pTooltip]="HINTS.charged">Charged</th>
                            <th class="cursor-help" [pTooltip]="HINTS.averageFee">Average fee</th>
                            <th class="cursor-help" [pTooltip]="HINTS.billed">Not billed</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-m>
                        <tr>
                            <td>{{ m.merchantCode || m.locationId }}</td>
                            <td class="tabular-nums">{{ m.deliveries }}</td>
                            <td class="tabular-nums">{{ m.currency }} {{ m.fees | number: '1.2-2' }}</td>
                            <td class="tabular-nums">{{ m.tips | number: '1.2-2' }}</td>
                            <td class="tabular-nums font-semibold">{{ m.paidToCourier | number: '1.2-2' }}</td>
                            <td class="tabular-nums">{{ m.chargedToCustomers | number: '1.2-2' }}</td>
                            <td class="tabular-nums">{{ m.averageFee | number: '1.2-2' }}</td>
                            <td class="tabular-nums">
                                @if (m.voidedDeliveries > 0) {
                                    {{ m.voidedDeliveries }} <span class="text-surface-500">({{ m.voidedAmount | number: '1.2-2' }})</span>
                                } @else { &mdash; }
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="8">No committed deliveries in this window.</td></tr>
                    </ng-template>
                </p-table>

                <!-- EVERY DELIVERY, on its own line. The totals answer "what do we owe"; this answers "which
                     deliveries make that up" — the only view that settles a courier invoice that disagrees. -->
                <div class="flex items-center gap-2 mt-4 mb-2">
                    <h6 class="m-0">Deliveries</h6>
                    <p-button [label]="showDetails() ? 'Hide' : 'Show'" size="small" [text]="true"
                              [loading]="detailsLoading()" (onClick)="toggleDetails()" />
                    <span class="text-xs text-surface-500">Each cost separated, newest first.</span>
                </div>

                @if (showDetails()) {
                    <p-table [value]="details()" styleClass="p-datatable-sm" [scrollable]="true" scrollHeight="24rem">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Accepted</th><th>Order</th><th>Merchant</th>
                                <th class="cursor-help" [pTooltip]="HINTS.fee">Quote fee</th>
                                <th class="cursor-help" [pTooltip]="HINTS.tip">Tip</th>
                                <th class="cursor-help" [pTooltip]="HINTS.paid">Paid</th>
                                <th class="cursor-help" [pTooltip]="HINTS.charged">Charged</th>
                                <th class="cursor-help" [pTooltip]="HINTS.absorbed">Absorbed</th>
                                <th class="cursor-help" [pTooltip]="HINTS.billed">Billed</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-d>
                            <tr [class.opacity-60]="d.voided">
                                <td class="whitespace-nowrap">{{ d.acceptedAt ? (d.acceptedAt | date: 'short') : '—' }}</td>
                                <td>{{ d.orderId || d.externalDeliveryId }}</td>
                                <td>{{ d.merchantCode || d.locationId }}</td>
                                <td class="tabular-nums">{{ d.currency }} {{ d.fee | number: '1.2-2' }}</td>
                                <td class="tabular-nums">{{ d.tip | number: '1.2-2' }}</td>
                                <td class="tabular-nums font-semibold">{{ d.paidToCourier | number: '1.2-2' }}</td>
                                <td class="tabular-nums">{{ d.chargedToCustomer | number: '1.2-2' }}</td>
                                <td class="tabular-nums" [class.text-orange-500]="d.absorbed > 0">
                                    @if (d.absorbed !== 0) { {{ d.absorbed | number: '1.2-2' }} } @else { &mdash; }
                                </td>
                                <td>
                                    @if (d.voided) {
                                        <p-tag [value]="d.voidReason || 'not billed'" severity="warn" />
                                    } @else { <p-tag value="billed" severity="success" /> }
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr><td colspan="9">No deliveries in this window.</td></tr>
                        </ng-template>
                    </p-table>
                }
            }
        </p-card>
        </p-tabpanel>

        <!-- THE NOTEBOOK. Mounted only once its tab is first opened: the first thing it asks the server is
             "is tracing on", and that question has no business being asked by someone who came here to
             paste a signing secret. -->
        <p-tabpanel value="diagnostics">
        @if (visited().has('diagnostics')) {
        <p-card>
            <ng-template pTemplate="title"><span>Quote diagnostics</span><i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Turn recording on, repeat the checkout, and each quote attempt appears below with DoorDash's answer." tooltipPosition="top"></i></ng-template>

            @if (traceError()) { <p-message severity="error" styleClass="mb-3">{{ traceError() }}</p-message> }

            @if (traceSettings(); as s) {
                @if (!s.configured) {
                    <!-- The flag is a column ON the connection row, so there is genuinely nowhere to put it
                         yet — said here rather than letting the switch fail with a 422. -->
                    <p-message severity="warn" styleClass="mb-3">
                        Save the DoorDash connection first.
                    </p-message>
                }

                <div class="flex items-start gap-3 mb-4">
                    <!-- Signal-bound rather than [(ngModel)] on a field: this page is zoneless, and the
                         switch has to visibly snap back when the server refuses the save. -->
                    <p-toggleswitch inputId="dd-trace" [ngModel]="tracingOn()"
                                    (ngModelChange)="tracingOn.set($event)"
                                    [disabled]="!s.configured || traceSaving()"
                                    (onChange)="setTracing($event.checked)" />
                    <div>
                        <label for="dd-trace" class="text-sm font-medium">Record quote attempts<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Records every quote attempt, including the customer's delivery address. Turn it off and clear the list when done." tooltipPosition="top"></i></label>
                        <!-- Said plainly, because this is a privacy decision and not a verbosity setting. -->
                        <div class="text-xs text-surface-500 mt-1">
                            {{ s.retained }} of {{ s.maxTraces }} held
                            @if (s.newestAt; as at) { · newest {{ at | date: 'medium' }} }
                        </div>
                    </div>
                </div>
            }

            <div class="flex flex-wrap items-center gap-2 mb-3">
                <p-select [options]="merchantOptions()" [(ngModel)]="traceLocationId" (onChange)="reloadTraces()"
                          placeholder="All merchants" [showClear]="true" [filter]="true" styleClass="w-64" />
                <p-select [options]="outcomeOptions" [(ngModel)]="traceOutcome" (onChange)="reloadTraces()"
                          placeholder="All outcomes" [showClear]="true" styleClass="w-44" />
                <p-button icon="pi pi-refresh" label="Refresh" size="small" [outlined]="true"
                          [loading]="tracesLoading()" (onClick)="reloadTraces()" />

                <!-- Two presses, no dialog: the list being deleted is a list of customers' homes, so the
                     sentence explaining that is the confirmation. -->
                @if (!confirmClear()) {
                    <p-button label="Clear traces" icon="pi pi-trash" size="small" [text]="true" severity="danger"
                              [disabled]="clearing()" (onClick)="confirmClear.set(true)" />
                } @else {
                    <span class="text-sm">Delete every stored trace?
                        <span class="text-surface-500">This cannot be undone.</span>
                    </span>
                    <p-button label="Confirm" icon="pi pi-trash" size="small" severity="warn"
                              [loading]="clearing()" (onClick)="clearTraces()" />
                    <p-button label="Cancel" size="small" [text]="true" (onClick)="confirmClear.set(false)" />
                }
            </div>

            @if (cleared() !== null) {
                <p-message severity="success" styleClass="mb-3">Deleted {{ cleared() }} trace(s).</p-message>
            }

            <p-table [value]="traces()" [loading]="tracesLoading()" [lazy]="true" [paginator]="true"
                     [rows]="tracePageSize" [totalRecords]="tracesTotal()" [first]="tracesFirst()"
                     (onLazyLoad)="onTracePage($event)" styleClass="p-datatable-sm">
                <ng-template pTemplate="header">
                    <tr>
                        <th>When</th><th>Merchant</th><th>Outcome</th><th>Fee</th>
                        <th class="cursor-help" [pTooltip]="DISTANCE_HINT">Distance</th>
                        <th>Took</th><th class="w-12"></th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-t>
                    <tr>
                        <td class="whitespace-nowrap text-sm">{{ t.occurredAt | date: 'short' }}</td>
                        <td class="text-sm">{{ t.merchantCode || t.locationId }}
                            @if (t.attempt > 1) {
                                <span class="text-surface-400 text-xs">· attempt {{ t.attempt }}</span>
                            }
                        </td>
                        <!-- Priced / a reasoned no / no answer at all. The third is a different problem from
                             the second and is coloured as one. -->
                        <td><p-tag [value]="t.outcome" [severity]="outcomeSev(t.outcome)" /></td>
                        <td class="tabular-nums">
                            @if (t.fee === null) { <span class="text-surface-400">&mdash;</span> }
                            @else { {{ t.currency }} {{ t.fee | number: '1.2-2' }} }
                        </td>
                        <td class="tabular-nums text-sm">
                            @if (t.distanceMiles === null) {
                                <span class="text-surface-400 cursor-help" [pTooltip]="DISTANCE_HINT">&mdash;</span>
                            } @else {
                                {{ t.distanceMiles | number: '1.1-1' }} mi
                                <span class="text-surface-400 text-xs">{{ t.distanceSource }}</span>
                            }
                        </td>
                        <td class="tabular-nums text-sm">{{ t.durationMs }} ms</td>
                        <td>
                            <p-button [icon]="expandedTrace() === t.id ? 'pi pi-chevron-up' : 'pi pi-code'"
                                      [text]="true" size="small" (onClick)="toggleTrace(t.id)" />
                        </td>
                    </tr>

                    <!-- The whole attempt, one row down. Addresses first because a wrong or unservable
                         address is the commonest cause of the refusal being read; the refusal itself
                         verbatim, because a paraphrase of a courier error has never once helped. -->
                    @if (expandedTrace() === t.id) {
                        <tr>
                            <td colspan="7">
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                    <div>
                                        <div class="text-xs font-semibold mb-1">Pickup<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="The store address DoorDash holds. Couriers collect from it." tooltipPosition="top"></i></div>
                                        <div class="text-sm">{{ t.pickupAddress || 'Not recorded' }}</div>
                                        @if (coords(t.pickupLatitude, t.pickupLongitude); as at) {
                                            <div class="text-xs text-surface-400 font-mono">{{ at }}</div>
                                        }
                                    </div>
                                    <div>
                                        <div class="text-xs font-semibold mb-1">Dropoff <span class="text-surface-400 font-normal">(customer address)</span></div>
                                        <div class="text-sm">{{ t.dropoffAddress || 'Not recorded' }}</div>
                                        @if (coords(t.dropoffLatitude, t.dropoffLongitude); as at) {
                                            <div class="text-xs text-surface-400 font-mono">{{ at }}</div>
                                        }
                                    </div>
                                </div>

                                @if (t.courierError) {
                                    <div class="text-xs font-semibold mb-1">DoorDash's answer, verbatim</div>
                                    <pre class="text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-64 mb-3">{{ t.courierError }}</pre>
                                }

                                <div class="text-xs text-surface-500 dark:text-surface-400 mb-3 flex flex-wrap gap-x-4 gap-y-1">
                                    <span><b>Delivery id:</b> <span class="font-mono">{{ t.externalDeliveryId || '—' }}</span></span>
                                    <span><b>Cart:</b> <span class="font-mono">{{ t.clientReference || '—' }}</span></span>
                                    <!-- Null is the INTERESTING case: a checkout that never became an order. -->
                                    <span><b>Order:</b> <span class="font-mono">{{ t.orderReference || 'never placed' }}</span></span>
                                    <span><b>Store:</b> <span class="font-mono">{{ t.storeId || '—' }}</span></span>
                                    <span><b>Business:</b> <span class="font-mono">{{ t.businessId || '—' }}</span></span>
                                    @if (t.estimatedPickupAt) { <span><b>Pickup est:</b> {{ t.estimatedPickupAt | date: 'short' }}</span> }
                                    @if (t.estimatedDropoffAt) { <span><b>Dropoff est:</b> {{ t.estimatedDropoffAt | date: 'short' }}</span> }
                                    @if (t.estimatedMinutes !== null) { <span><b>Estimated:</b> {{ t.estimatedMinutes }} min</span> }
                                </div>

                                <!-- WIRE BY DEFAULT, SUMMARY ON REQUEST. Labelled halves, as everywhere
                                     else raw evidence is shown: side by side and unlabelled, a request and
                                     a response are easy to mistake mid-incident.

                                     The summary is composed in OUR vocabulary (orderValue: 75.55, dollars)
                                     while DoorDash is sent order_value: 7555 in cents — so a pane labelled
                                     only "Asked" invited exactly the wrong conclusion about what the
                                     courier received. The wire bodies answer that question and lead;
                                     the summary stays one click away because it is the faster read. -->
                                <div class="flex items-center gap-2 mb-2">
                                    <p-selectButton [options]="traceViewOptions" [ngModel]="traceView()"
                                                    (ngModelChange)="traceView.set($event)"
                                                    [allowEmpty]="false" styleClass="text-xs" />
                                    @if (traceView() === 'wire' && !t.requestBody && !t.responseBody) {
                                        <span class="text-xs text-surface-500 dark:text-surface-400">
                                            Not recorded for this trace.
                                        </span>
                                    }
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <div class="text-xs font-semibold mb-1">
                                            {{ traceView() === 'wire' ? 'Sent to DoorDash' : 'Asked (our summary)' }}
                                        </div>
                                        @if (tracePane(t, 'request'); as body) {
                                            <pre class="text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-96">{{ pretty(body) }}</pre>
                                        } @else {
                                            <p class="text-xs text-surface-500 dark:text-surface-400">Not recorded.</p>
                                        }
                                    </div>
                                    <div>
                                        <div class="text-xs font-semibold mb-1">
                                            {{ traceView() === 'wire' ? "DoorDash's reply" : 'Answered (our summary)' }}
                                        </div>
                                        @if (tracePane(t, 'response'); as body) {
                                            <pre class="text-xs bg-surface-100 dark:bg-surface-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-96">{{ pretty(body) }}</pre>
                                        } @else {
                                            <p class="text-xs text-surface-500 dark:text-surface-400">Not recorded.</p>
                                        }
                                    </div>
                                </div>
                            </td>
                        </tr>
                    }
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr><td colspan="7">{{ emptyTraces() }}</td></tr>
                </ng-template>
            </p-table>
        </p-card>
        }
        </p-tabpanel>
        </p-tabpanels>
        </p-tabs>
    `
})
export class DoorDashIntegration implements OnInit {
    private readonly api = inject(MiddlewareApi);

    /** The spend glossary, one tooltip per label — shared with the courier-costs page so the wording cannot drift. */
    readonly HINTS = SPEND_HINTS;

    /** Why the distance column is so often empty, said once. Nothing in the quote flow geocodes: the pickup
     *  travels as a store id and the dropoff as a typed postal address, so a mileage exists only when both
     *  sides happen to carry coordinates or DoorDash volunteers a distance of its own. */
    readonly DISTANCE_HINT =
        'Straight-line miles when both ends have coordinates, otherwise the distance DoorDash returned. Blank is normal.';

    // The four jobs this page holds. Connection is first because it is what the page was; Diagnostics is
    // last and mounts only when opened (see the @if in the template), so nothing here changes for anyone
    // who never opens it. `visited` is monotonic — a mounted tab keeps its filters and paging.
    /// WIRE OR SUMMARY for the expanded trace panes. Defaults to the wire: the summary is this platform's
    /// paraphrase in its own units, and the question an operator opens a trace to answer is almost always
    /// "what did DoorDash actually receive".
    readonly traceView = signal<'wire' | 'summary'>('wire');
    readonly traceViewOptions = [
        { label: 'Wire', value: 'wire' },
        { label: 'Summary', value: 'summary' }
    ];

    readonly activeTab = signal<string>('connection');
    readonly visited = signal<Set<string>>(new Set(['connection']));

    readonly connection = signal<DoorDashConnection | null>(null);
    readonly loadError = signal<string | null>(null);
    readonly saveError = signal<string | null>(null);
    readonly saved = signal(false);
    readonly saving = signal(false);

    /** DoorDash Drive, as this estate's own integration has always addressed it. Sandbox prices quotes and
     *  never dispatches a dasher; production sends a real one — which is why an unsaved connection starts on
     *  sandbox and switching is a deliberate click. */
    readonly SANDBOX_URL = 'https://openapi.doordash.com/drive/v2/';
    readonly PRODUCTION_URL = 'https://api.doordash.com/drive/v2/';

    apiBaseUrl = '';
    developerId = '';
    keyId = '';
    signingSecret = '';
    externalBusinessId = '';
    quoteValidMinutes = 5;
    idPrefix = '';

    readonly locations = signal<OnboardedLocation[]>([]);
    readonly locationsLoading = signal(false);
    readonly copied = signal(false);

    /** Composed from the API base this dashboard already talks to, so it cannot drift from reality. */
    readonly webhookUrl = `${environment.apiBaseUrl.replace(/\/$/, '')}/api/webhooks/doordash`;
    readonly stores = signal<DeliveryStoreRow[]>([]);
    readonly verifying = signal(false);
    readonly registering = signal<string | null>(null);
    readonly storeError = signal<string | null>(null);

    /** The push in flight, and how the last one ended — both by location id, because this table has one row
     *  per merchant and "it failed" without saying which row is not an error message. Only one update runs
     *  at a time (the buttons disable while one is in flight), so one slot each is the honest shape. */
    readonly updating = signal<string | null>(null);
    readonly updated = signal<string | null>(null);
    readonly updateFailed = signal<{ locationId: string; message: string } | null>(null);

    /** Said on the button itself, because "update" is ambiguous about direction and getting it backwards
     *  here would mean overwriting the estate's merchant record with a courier's stale copy. */
    readonly UPDATE_HINT =
        "Sends this merchant's name, phone and address to DoorDash, replacing what DoorDash holds.";

    readonly costs = signal<DeliveryCostReport | null>(null);
    readonly costsLoading = signal(false);
    readonly details = signal<DeliveryCostDetail[]>([]);
    readonly detailsLoading = signal(false);
    readonly showDetails = signal(false);
    readonly costError = signal<string | null>(null);
    from = '';
    to = '';

    // --- Quote diagnostics -------------------------------------------------------------------------
    readonly traceSettings = signal<DeliveryTraceSettings | null>(null);
    readonly traceSaving = signal(false);
    readonly traceError = signal<string | null>(null);
    readonly traces = signal<DeliveryTraceRow[]>([]);
    readonly tracesTotal = signal(0);
    readonly tracesLoading = signal(false);
    readonly tracesFirst = signal(0);
    /** Expanded by row ID, not by index: the table is paged server-side and an index means a different
     *  attempt after every page turn. */
    readonly expandedTrace = signal<string | null>(null);
    readonly confirmClear = signal(false);
    readonly clearing = signal(false);
    readonly cleared = signal<number | null>(null);
    readonly tracePageSize = 50;

    /** The switch's own copy of the flag, as a SIGNAL. A refused save puts it back where the server left
     *  it, and a switch left showing ON after the server said no is the one lie this screen must not tell —
     *  so the rollback has to repaint on its own account. As a plain field it repainted only because the
     *  error signal happened to be written in the same handler; the day someone made that write conditional,
     *  the switch would have started lying. This page is zoneless: what is read in a template is a signal. */
    readonly tracingOn = signal(false);
    traceLocationId: string | null = null;
    traceOutcome: string | null = null;

    /** The trace request in flight, as page + filters.
     *
     *  A lazy table has TWO things that can ask for the same page: this component (Refresh, a filter
     *  change) and PrimeNG's own onLazyLoad. Which of the two fires when `[first]` is moved back to zero is
     *  an internal detail of the table — in 21.1.9 setting the input does not emit, in other versions it
     *  has — and `reloadTraces` used to both reset the page AND fetch it, so it was correct only by
     *  accident of the version installed. This makes it not matter: the second ask for a page already being
     *  fetched is dropped, and no ask is ever silently lost. */
    private tracesInFlight: string | null = null;

    readonly outcomeOptions = [
        { label: 'Quoted', value: 'quoted' },
        { label: 'Refused', value: 'refused' },
        { label: 'Error', value: 'error' }
    ];

    /** The merchants already loaded for the stores table, as filter options — no second request. */
    readonly merchantOptions = computed(() =>
        this.locations().map((l) => ({ label: `${l.name || l.merchantCode || l.locationId}`, value: l.locationId })));

    ngOnInit(): void {
        this.api.getDoorDashConnection().subscribe({
            next: (c) => {
                this.connection.set(c);
                // Prefilled, not merely hinted: a placeholder leaves the field empty, and an empty base URL
                // fails the first save with "apiBaseUrl is required" for no reason anyone can see.
                this.apiBaseUrl = c.apiBaseUrl || this.SANDBOX_URL;
                this.developerId = c.developerId;
                this.keyId = c.keyId;
                this.externalBusinessId = c.externalBusinessId ?? '';
                this.quoteValidMinutes = c.quoteValidMinutes || 5;
                this.idPrefix = c.idPrefix ?? '';
            },
            error: (e) => this.loadError.set(this.describe(e))
        });

        this.locationsLoading.set(true);
        this.api.getLocations().subscribe({
            next: (r) => {
                this.locations.set(r.locations ?? []);
                this.locationsLoading.set(false);
            },
            error: (e) => {
                this.locationsLoading.set(false);
                this.storeError.set(this.describe(e));
            }
        });

        // EVERY MERCHANT IS CHECKED AT DOORDASH ON LOAD. It is one developer-API call per merchant, which is
        // why this was a button first — but "do we hold an id" is not the question anyone opens this page to
        // ask. The answer that matters is whether DoorDash knows the store, because a merchant it does not
        // know fails at checkout. The button stays as a refresh.
        this.loadStores(true);
        this.loadCosts();
    }

    onTabChange(value: string | number | undefined): void {
        const tab = String(value ?? 'connection');
        this.activeTab.set(tab);
        if (!this.visited().has(tab)) {
            this.visited.update((s) => new Set(s).add(tab));
            // The switch's state, asked for the first time the notebook is opened and not before. The
            // traces themselves are fetched by the table's own first lazy load.
            if (tab === 'diagnostics') this.loadTraceSettings();
        }
    }

    /** Whether tracing is on, and how much has piled up behind it. */
    loadTraceSettings(): void {
        this.api.getDeliveryTraceSettings().subscribe({
            next: (s) => {
                this.traceSettings.set(s);
                this.tracingOn.set(s.enabled);
            },
            error: (e) => this.traceError.set(this.describe(e))
        });
    }

    /** Flips tracing server-side. Its own route, so turning a debug switch on never means re-posting the
     *  signing secret through the credential form. */
    setTracing(enabled: boolean): void {
        this.traceSaving.set(true);
        this.traceError.set(null);
        this.api.setDeliveryTracing(enabled).subscribe({
            next: () => {
                this.traceSaving.set(false);
                // Re-read rather than assume: the counts move on their own as attempts arrive.
                this.loadTraceSettings();
            },
            error: (e) => {
                this.traceSaving.set(false);
                // Put the switch back where the server left it — a signal write, so it repaints on its own
                // rather than riding on the error message being set beside it.
                this.tracingOn.set(!enabled);
                this.traceError.set(this.describe(e));
            }
        });
    }

    onTracePage(e: { first?: number | null; rows?: number | null }): void {
        const first = e.first ?? 0;
        this.tracesFirst.set(first);
        this.loadTraces(Math.floor(first / this.tracePageSize) + 1);
    }

    /** Back to page one — what every filter change and the Refresh button mean.
     *
     *  Both halves are needed and neither is a duplicate of the other: the paginator has to be moved back
     *  to the first page (it is showing "51-100 of …" of a list that no longer exists), and page one has to
     *  be fetched. If moving `[first]` also makes the table ask, that ask is dropped by `loadTraces` as a
     *  request already in flight. */
    reloadTraces(): void {
        this.tracesFirst.set(0);
        this.cleared.set(null);
        this.loadTraces(1);
        // The header counts age as fast as the table does.
        this.loadTraceSettings();
    }

    private loadTraces(page: number): void {
        // Same page, same filters, already on the wire: whoever asked second is asking for what is already
        // coming. See `tracesInFlight` — this is what makes the table's own lazy load and the component's
        // explicit reload safe to both exist.
        const asking = `${page}|${this.traceLocationId ?? ''}|${this.traceOutcome ?? ''}`;
        if (this.tracesInFlight === asking) return;
        this.tracesInFlight = asking;

        this.tracesLoading.set(true);
        this.traceError.set(null);
        this.expandedTrace.set(null);
        this.api.getDeliveryTraces({
            locationId: this.traceLocationId || undefined,
            outcome: this.traceOutcome || undefined,
            page,
            pageSize: this.tracePageSize
        }).subscribe({
            next: (r) => {
                this.tracesInFlight = null;
                this.traces.set(r.rows);
                this.tracesTotal.set(r.total);
                this.tracesLoading.set(false);
            },
            error: (e) => {
                this.tracesInFlight = null;
                this.tracesLoading.set(false);
                this.traceError.set(this.describe(e));
            }
        });
    }

    clearTraces(): void {
        this.clearing.set(true);
        this.traceError.set(null);
        this.api.clearDeliveryTraces().subscribe({
            next: (r) => {
                this.clearing.set(false);
                this.confirmClear.set(false);
                this.cleared.set(r.deleted);
                this.traces.set([]);
                this.tracesTotal.set(0);
                this.tracesFirst.set(0);
                this.expandedTrace.set(null);
                this.loadTraceSettings();
            },
            error: (e) => {
                this.clearing.set(false);
                this.confirmClear.set(false);
                this.traceError.set(this.describe(e));
            }
        });
    }

    toggleTrace(id: string): void {
        this.expandedTrace.set(this.expandedTrace() === id ? null : id);
    }

    outcomeSev(outcome: string): 'success' | 'warn' | 'danger' | 'secondary' {
        switch (outcome) {
            case 'Quoted': return 'success';
            // A reasoned no. Amber, not red: DoorDash answered, and the answer is in the row.
            case 'Refused': return 'warn';
            // No answer at all — unreachable, timed out, or a blank error. A different problem entirely.
            case 'Error': return 'danger';
            default: return 'secondary';
        }
    }

    /** A coordinate pair, or nothing. 0,0 never reaches here — the recorder rejects it as unplaceable
     *  rather than measuring from the Atlantic. */
    coords(lat: number | null, lng: number | null): string | null {
        if (lat === null || lng === null) return null;
        return `${lat}, ${lng}`;
    }

    /** An empty table means two very different things, and saying which saves the wrong conclusion. */
    emptyTraces(): string {
        const settings = this.traceSettings();
        if (settings && !settings.enabled) {
            return 'Tracing is off — nothing is being recorded. Switch it on above, then reproduce the checkout.';
        }
        if (this.traceLocationId || this.traceOutcome) {
            return 'No traced attempts match this filter.';
        }
        return 'No quote attempts traced yet. Reproduce the checkout while tracing is on.';
    }

    /** The body for one pane under the current view. Falls back to the summary when the wire body is
     *  absent — traces recorded before the bodies were kept would otherwise render two empty panes and
     *  look like a broken screen rather than an older record. */
    tracePane(t: DeliveryTraceRow, half: 'request' | 'response'): string | null {
        const wire = half === 'request' ? t.requestBody : t.responseBody;
        const summary = half === 'request' ? t.requestSummary : t.responseSummary;
        return this.traceView() === 'wire' ? (wire ?? summary) : summary;
    }

    pretty(raw: string): string {
        try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
    }

    /** Shows the per-delivery breakdown, fetching it the first time it is asked for. */
    toggleDetails(): void {
        if (this.showDetails()) {
            this.showDetails.set(false);
            return;
        }

        this.showDetails.set(true);
        this.detailsLoading.set(true);
        this.api.getDeliveryCostDetails({ from: this.from || undefined, to: this.to || undefined }).subscribe({
            next: (rows) => {
                this.details.set(rows);
                this.detailsLoading.set(false);
            },
            error: (e) => {
                this.detailsLoading.set(false);
                this.costError.set(this.describe(e));
            }
        });
    }

    copyWebhookUrl(): void {
        navigator.clipboard?.writeText(this.webhookUrl).then(
            () => {
                this.copied.set(true);
                setTimeout(() => this.copied.set(false), 2000);
            },
            () => { /* a clipboard the browser refuses is not worth an error banner */ }
        );
    }

    loadStores(verify: boolean): void {
        if (verify) this.verifying.set(true);
        // A fresh read answers both of these on its own — leaving "Sent" or a stale failure beside a row
        // that has just been re-checked would be reporting the previous page load.
        this.updated.set(null);
        this.updateFailed.set(null);
        this.api.getDeliveryStores(verify).subscribe({
            next: (rows) => {
                this.stores.set(rows);
                this.verifying.set(false);
            },
            error: (e) => {
                this.verifying.set(false);
                this.storeError.set(this.describe(e));
            }
        });
    }

    /** Whether this merchant still has to be registered. Anything DoorDash confirmed — active, inactive or
     *  simply registered — is NOT that, and neither is a state we could not check: offering Register on an
     *  unknown answer is how a live store gets overwritten by a guess. */
    needsRegistering(locationId: string): boolean {
        const status = this.storeRow(locationId)?.status ?? 'not_configured';
        return status === 'not_configured' || status === 'not_found';
    }

    /** Whether DoorDash's registered address disagrees with the merchant record. Compared loosely — case,
     *  punctuation and spacing differ harmlessly between two systems, and flagging those would train everyone
     *  to ignore the warning that matters. */
    addressDiffers(location: OnboardedLocation, row: DeliveryStoreRow): boolean {
        if (!row.registeredAddress || !location.address) return false;
        const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
        return normalise(row.registeredAddress) !== normalise(location.address);
    }

    storeRow(locationId: string): DeliveryStoreRow | undefined {
        return this.stores().find((s) => s.locationId === locationId);
    }

    storeStatusLabel(locationId: string): string {
        const status = this.storeRow(locationId)?.status ?? 'not_configured';
        return {
            not_configured: 'no store id',
            configured: 'id set, unchecked',
            active: 'registered · active',
            inactive: 'registered · inactive',
            registered: 'registered',
            not_found: 'not at DoorDash',
            cannot_verify: 'cannot check'
        }[status] ?? status;
    }

    storeStatusSev(locationId: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        switch (this.storeRow(locationId)?.status) {
            case 'active':
            case 'registered': return 'success';
            case 'inactive': return 'warn';
            // Not a cosmetic state: this merchant cannot be quoted, so it reads as a failure.
            case 'not_found': return 'danger';
            case 'configured': return 'info';
            default: return 'secondary';
        }
    }

    /** Registers the store at DoorDash from the merchant record we already hold. */
    register(location: OnboardedLocation): void {
        this.registering.set(location.locationId);
        this.storeError.set(null);
        this.api.registerDeliveryStore(location.locationId, {
            // No storeId: the middleware derives it and REFUSES a differing one, so sending anything
            // here is at best redundant and at worst a 422 on the operator's only recovery lever.
            storeId: null,
            name: location.name || location.merchantCode || location.locationId,
            phone: location.phone || '',
            // The middleware holds a location's Magento identity, not its address — so it travels from here,
            // where the merchant record already is.
            street: location.address || '',
            city: '',
            state: '',
            postalCode: '',
            country: 'US'
        }).subscribe({
            next: (row) => {
                this.registering.set(null);
                this.stores.update((rows) => [...rows.filter((r) => r.locationId !== row.locationId), row]);
            },
            error: (e) => {
                this.registering.set(null);
                this.storeError.set(this.describe(e));
            }
        });
    }

    /** Pushes the merchant record we hold UP to DoorDash's copy of the store.
     *
     *  The direction is the whole point: the list can only report that the two have drifted, and the copy
     *  that drifted is the one a dasher is actually sent to. The same merchant-record fields Register sends,
     *  so a store created here and a store corrected here carry identical details.
     *
     *  Safe to press on a store DoorDash does not have — the server answers with "press Register" rather
     *  than a courier 404 — but the button is not offered in that case anyway. */
    updateDetails(location: OnboardedLocation): void {
        this.updating.set(location.locationId);
        this.updated.set(null);
        this.updateFailed.set(null);
        this.api.updateDeliveryStoreDetails(location.locationId, {
            name: location.name || location.merchantCode || location.locationId,
            phone: location.phone || '',
            // One composed line, exactly as Register sends it — the middleware splits it and puts the
            // country back on, because DoorDash geocodes one otherwise and refuses the mismatch.
            street: location.address || '',
            city: '',
            state: '',
            postalCode: '',
            country: 'US'
        }).subscribe({
            next: (row) => {
                this.updating.set(null);
                this.updated.set(row.locationId);
                // Replaced with DOORDASH'S post-update copy, not with what we sent: a field the courier
                // silently declined has to keep showing as different, or this button would appear to work
                // while the dasher still went to the old address.
                this.stores.update((rows) => rows.map((r) => (r.locationId === row.locationId ? row : r)));
            },
            error: (e) => {
                this.updating.set(null);
                this.updateFailed.set({ locationId: location.locationId, message: this.describe(e) });
            }
        });
    }

    save(): void {
        this.saving.set(true);
        this.saveError.set(null);
        this.saved.set(false);
        this.api.upsertDoorDashConnection({
            apiBaseUrl: this.apiBaseUrl.trim(),
            developerId: this.developerId.trim(),
            keyId: this.keyId.trim(),
            // Blank = keep the stored secret. Sending an empty string would be indistinguishable from
            // "clear it", which is not an operation this form offers.
            signingSecret: this.signingSecret.trim() ? this.signingSecret.trim() : null,
            externalBusinessId: this.externalBusinessId.trim() || null,
            quoteValidMinutes: this.quoteValidMinutes,
            idPrefix: this.idPrefix.trim() || null
        }).subscribe({
            next: () => {
                this.saving.set(false);
                this.saved.set(true);
                this.signingSecret = '';
                // Re-read so the "set" tag and the timestamp reflect the server, not this form's hopes.
                this.api.getDoorDashConnection().subscribe({ next: (c) => this.connection.set(c) });
            },
            error: (e) => {
                this.saving.set(false);
                this.saveError.set(this.describe(e));
            }
        });
    }

    loadCosts(): void {
        this.costsLoading.set(true);
        this.costError.set(null);
        this.api.getDeliveryCosts({
            from: this.from ? new Date(this.from).toISOString() : undefined,
            to: this.to ? new Date(this.to).toISOString() : undefined
        }).subscribe({
            next: (r) => {
                this.costs.set(r);
                this.costsLoading.set(false);
            },
            error: (e) => {
                this.costsLoading.set(false);
                this.costError.set(this.describe(e));
            }
        });
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 403) return 'Forbidden — this page needs the instances:manage permission (the same one the XT connection uses).';
        return e?.error?.message || `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
